// v3.11 — Locks the preset chips behind a confirm() so an accidental
// click can't rewrite a live pattern. Adds a "dirty" indicator + Discard
// button so the operator can see when the form has drifted from what's
// actually saved in the DB.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Hash, RefreshCw, AlertCircle, Lock } from "lucide-react";

const PRESETS = [
  { label: "ArmorBatch default", pattern: "INV-{yymm}-{seq:04d}", preview: "INV-2608-0001" },
  { label: "uBatch legacy (I26080001)", pattern: "I{yymm}{seq:04d}", preview: "I26080001" },
  { label: "uBatch classic (I000428CA)", pattern: "I{seq:06d}CA", preview: "I000428CA" },
  { label: "By year (2026/00042)", pattern: "{yyyy}/{seq:05d}", preview: "2026/00042" },
];

export default function InvoiceNumberingCard() {
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState(null);
  const [invPattern, setInvPattern] = useState("");
  const [csPattern, setCsPattern] = useState("");
  const [invSeq, setInvSeq] = useState("");
  const [csSeq, setCsSeq] = useState("");
  const [unified, setUnified] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/settings/invoice-numbering");
      setState(data);
      setInvPattern(data.invoice_pattern);
      setCsPattern(data.cash_sale_pattern);
      setInvSeq(String(data.invoice_next_seq));
      setCsSeq(String(data.cash_sale_next_seq));
      setUnified(!!data.unified_numbering);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load invoice numbering");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/invoice-numbering", {
        invoice_pattern: invPattern,
        cash_sale_pattern: csPattern,
        invoice_next_seq: parseInt(invSeq, 10) || 1,
        cash_sale_next_seq: parseInt(csSeq, 10) || 1,
        unified_numbering: unified,
      });
      setState(data);
      toast.success(`Saved — next invoice will be ${data.invoice_preview}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  // v3.11 — Detect unsaved changes so we can warn the operator before
  // they hit Save and clobber a live sequence.
  const dirty =
    invPattern !== state.invoice_pattern ||
    csPattern !== state.cash_sale_pattern ||
    (parseInt(invSeq, 10) || 1) !== state.invoice_next_seq ||
    (parseInt(csSeq, 10) || 1) !== state.cash_sale_next_seq ||
    unified !== !!state.unified_numbering;

  const guardedSave = async () => {
    if (dirty) {
      const nextPreview = previewOf(invPattern, parseInt(invSeq, 10) || 1);
      const ok = window.confirm(
        `Save invoice numbering changes?\n\n` +
        `Next invoice will be:  ${nextPreview}\n` +
        (unified ? `EFT sales will share this counter.\n` : `EFT sales stay on their own counter.\n`) +
        `\nOK to overwrite, Cancel to bail.`
      );
      if (!ok) return;
    }
    await save();
  };

  return (
    <div className="border border-industrial bg-surface p-5" data-testid="invoice-numbering-card">
      <div className="flex items-center gap-2 mb-3">
        <Hash className="w-4 h-4 text-primary-orange" />
        <div className="label-tag">Invoice Numbering</div>
        {dirty && (
          <div className="ml-auto flex items-center gap-1 border border-amber-500 bg-amber-950/20 px-2 py-0.5" data-testid="inv-numbering-dirty-badge">
            <AlertCircle className="w-3 h-3 text-amber-400" />
            <span className="mono text-[10px] uppercase tracking-widest text-amber-300">Unsaved changes</span>
          </div>
        )}
      </div>
      {dirty && (
        <div className="border border-amber-500 bg-amber-950/10 p-2.5 mb-4 flex items-start gap-2" data-testid="inv-numbering-dirty-banner">
          <AlertCircle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
          <div className="mono text-[11px] text-amber-200 flex-1">
            <div>Form values differ from the saved config in the database.</div>
            <div className="text-amber-400 mt-0.5">Live saved: <b>{state.invoice_pattern}</b> · next <b>{state.invoice_preview}</b>{state.unified_numbering ? " · unified" : ""}. Click <b>Discard</b> to reload the live values, or Save to overwrite.</div>
          </div>
        </div>
      )}
      <div className="text-xs text-muted-industrial mb-4">
        Configure how invoice numbers look. Tokens: <code className="mono">{"{seq}"}</code>, <code className="mono">{"{seq:04d}"}</code>, <code className="mono">{"{yy}"}</code>, <code className="mono">{"{yyyy}"}</code>, <code className="mono">{"{mm}"}</code>, <code className="mono">{"{yymm}"}</code>. Reset next-sequence to continue from your legacy system.
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {PRESETS.map((p) => (
          <button
            key={p.pattern}
            type="button"
            onClick={() => {
              // v3.11 — Locked behind a confirm to stop accidental clicks
              // during morning rush from rewriting the live pattern.
              const currentInv = state?.invoice_pattern || "";
              const nextInv = p.pattern;
              if (currentInv && currentInv !== nextInv) {
                const ok = window.confirm(
                  `Replace your saved pattern "${currentInv}" with the preset "${nextInv}"?\n\n` +
                  `This ONLY changes the form. Your saved config stays as-is until you click "Save Numbering". ` +
                  `Click Cancel to keep the current pattern.`
                );
                if (!ok) return;
              }
              setInvPattern(nextInv);
              setCsPattern(nextInv);
            }}
            data-testid={`inv-preset-${p.pattern}`}
            className="border border-industrial hover:border-primary-orange hover:text-primary-orange px-2.5 py-1.5 text-[10px] mono uppercase tracking-wider flex items-center gap-1"
            title={`${p.pattern} — click asks for confirmation first`}
          >
            <Lock className="w-2.5 h-2.5 opacity-60" />
            {p.label} → <span className="text-primary-orange">{p.preview}</span>
          </button>
        ))}
      </div>

      <label className="flex items-start gap-2 mb-4 cursor-pointer border border-industrial p-2.5 bg-surface-2 hover:border-primary-orange transition" data-testid="unified-numbering-label">
        <input
          type="checkbox"
          checked={unified}
          onChange={(e) => setUnified(e.target.checked)}
          data-testid="unified-numbering-cb"
          className="mt-0.5"
        />
        <div className="flex-1">
          <div className="mono text-xs uppercase tracking-widest font-bold text-ink">Unified numbering</div>
          <div className="mono text-[10px] text-muted-industrial mt-0.5">
            When ON: EFT sales draw from the same counter and pattern as regular invoices — one running sequence for everything. Turn OFF to keep two separate series.
          </div>
        </div>
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="label-tag mb-1">Regular invoice pattern</Label>
          <Input
            value={invPattern}
            onChange={(e) => setInvPattern(e.target.value)}
            data-testid="inv-pattern-input"
            className="rounded-none mono bg-surface-2 border-industrial"
            spellCheck={false}
          />
          <div className="text-[10px] text-muted-industrial mono mt-1">Preview: <span className="text-primary-orange">{previewOf(invPattern, parseInt(invSeq, 10) || 1)}</span></div>
        </div>
        <div>
          <Label className="label-tag mb-1">EFT-sale invoice pattern {unified && <span className="text-[10px] normal-case text-muted-industrial">· (ignored — unified)</span>}</Label>
          <Input
            value={csPattern}
            onChange={(e) => setCsPattern(e.target.value)}
            data-testid="cs-pattern-input"
            disabled={unified}
            className={`rounded-none mono bg-surface-2 border-industrial ${unified ? "opacity-40" : ""}`}
            spellCheck={false}
          />
          <div className="text-[10px] text-muted-industrial mono mt-1">Preview: <span className="text-primary-orange">{previewOf(unified ? invPattern : csPattern, parseInt(unified ? invSeq : csSeq, 10) || 1)}</span></div>
        </div>
        <div>
          <Label className="label-tag mb-1">Next regular seq (reset here)</Label>
          <Input
            value={invSeq}
            onChange={(e) => setInvSeq(e.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            data-testid="inv-next-seq"
            className="rounded-none mono bg-surface-2 border-industrial"
          />
          <div className="text-[10px] text-muted-industrial mono mt-1">Next invoice: <span className="text-primary-orange">{state.invoice_preview}</span></div>
        </div>
        <div>
          <Label className="label-tag mb-1">Next EFT-sale seq (reset here) {unified && <span className="text-[10px] normal-case text-muted-industrial">· (ignored — unified)</span>}</Label>
          <Input
            value={csSeq}
            onChange={(e) => setCsSeq(e.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            disabled={unified}
            data-testid="cs-next-seq"
            className={`rounded-none mono bg-surface-2 border-industrial ${unified ? "opacity-40" : ""}`}
          />
          <div className="text-[10px] text-muted-industrial mono mt-1">Next EFT sale: <span className="text-primary-orange">{state.cash_sale_preview}</span></div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 mt-4">
        <Button variant="ghost" onClick={load} disabled={busy} data-testid="inv-numbering-reload" className="rounded-none mono uppercase text-xs tracking-widest">
          <RefreshCw className="w-3 h-3 mr-1" />{dirty ? "Discard" : "Reload"}
        </Button>
        <Button
          onClick={guardedSave}
          disabled={busy || !dirty}
          data-testid="inv-numbering-save"
          className={`rounded-none uppercase tracking-widest text-xs font-bold ${dirty ? "bg-primary-orange hover:bg-[rgb(var(--primary-hover))] text-black" : "bg-surface-2 text-muted-industrial border border-industrial cursor-not-allowed"}`}
        >
          {busy ? "Saving…" : dirty ? "Save numbering" : "Saved · in sync"}
        </Button>
      </div>
    </div>
  );
}

function previewOf(pattern, seq) {
  const d = new Date();
  const yy = String(d.getFullYear()).slice(2);
  const yyyy = String(d.getFullYear());
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yymm = `${yy}${mm}`;
  const yyyymm = `${yyyy}${mm}`;
  const s = seq || 1;
  try {
    // Handle {seq:04d} / {seq:06d} style widths
    return (pattern || "")
      .replace(/\{seq:(\d+)d\}/g, (_, w) => String(s).padStart(parseInt(w, 10), "0"))
      .replace(/\{seq\}/g, String(s))
      .replace(/\{yymm\}/g, yymm)
      .replace(/\{yyyymm\}/g, yyyymm)
      .replace(/\{yyyy\}/g, yyyy)
      .replace(/\{yy\}/g, yy)
      .replace(/\{mm\}/g, mm)
      .replace(/\{dd\}/g, dd);
  } catch { return pattern; }
}
